import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import {
  NotificationSubscriptionRequest,
  SignInFormControl,
  SignInUserData,
  SignUpFormControl,
  ValidationEmailResult,
} from '../models/authentication.model';

export interface RefreshTokenRequest {
  refreshToken: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationApiService {
  private readonly http = inject(HttpClient);

  public signUp(authentication: SignUpFormControl): Observable<SignInUserData> {
    const url = `${environment.host}:${environment.port}/api/authentication/sign_up/`;
    return this.http.post<SignInUserData>(url, authentication);
  }

  public validationEmail(token: string): Observable<ValidationEmailResult> {
    const url = `${environment.host}:${environment.port}/api/authentication/validation_email/`;
    return this.http.post<ValidationEmailResult>(url, {
      token: token,
    });
  }

  /**
   * Exchange a Google Identity Services credential (a JWT) for a SKISS session.
   * Backend: POST /api/authentication/google_sign_in/
   */
  public googleSignIn(
    credential: string,
    userSettings: Record<string, string>
  ): Observable<SignInUserData> {
    const url = `${environment.host}:${environment.port}/api/authentication/google_sign_in/`;
    return this.http.post<SignInUserData>(url, {
      credential,
      user_settings: userSettings,
      audience: 'platform',
    });
  }

  public signIn(authentication: SignInFormControl): Observable<SignInUserData> {
    const url = `${environment.host}:${environment.port}/api/authentication/sign_in/`;
    // Named here rather than at the call sites so every route into sign-in carries it.
    // The token minted for this audience is refused on backoffice surfaces.
    return this.http.post<SignInUserData>(url, { ...authentication, audience: 'platform' });
  }

  /**
   * Refresh access token using refresh token
   * Based on the old authentication.service.ts refresh token logic
   * @param refreshToken The refresh token to use
   * @returns Observable<string> - new access token as string
   */
  public refreshToken(refreshToken: string): Observable<string> {
    const url = `${environment.host}:${environment.port}/api/authentication/refresh_token/`;
    const request: RefreshTokenRequest = { refreshToken };
    return this.http.post<string>(url, request, { responseType: 'json' });
  }

  public subscribeNotification(subscription: NotificationSubscriptionRequest): Observable<boolean> {
    const url = `${environment.host}:${environment.port}/api/authentication/subscribe_notification/`;
    // Auth interceptor will automatically add the Authorization header
    return this.http.post<boolean>(url, subscription, { responseType: 'json' });
  }

  public requestPasswordReset(email: string): Observable<void> {
    const url = `${environment.host}:${environment.port}/api/authentication/request_password_reset/`;
    return this.http.post<void>(url, { email });
  }

  public resetPassword(token: string, newPassword: string): Observable<void> {
    const url = `${environment.host}:${environment.port}/api/authentication/reset_password/`;
    return this.http.post<void>(url, { token, new_password: newPassword });
  }

  public changePassword(currentPassword: string, newPassword: string): Observable<void> {
    const url = `${environment.host}:${environment.port}/api/authentication/change_password/`;
    return this.http.post<void>(url, {
      current_password: currentPassword,
      new_password: newPassword,
    });
  }

  public validateSetPasswordToken(token: string): Observable<{ email: string }> {
    const url = `${environment.host}:${environment.port}/api/authentication/validate_set_password_token/`;
    return this.http.get<{ email: string }>(url, { params: { token } });
  }

  public setPasswordWithToken(token: string, newPassword: string): Observable<SignInUserData> {
    const url = `${environment.host}:${environment.port}/api/authentication/set_password_with_token/`;
    return this.http.post<SignInUserData>(url, { token, new_password: newPassword });
  }
}
